import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { buildDayEvents, mapCanonicalToViewModel } from "@/lib/dayEvents";
import type { CityContent, EngineItinerary, EngineStay, EngineLeg, ItineraryMode, DecisionReport, TravelFrictionReport, TripTimeline, CanonicalItinerary } from "@/lib/types";
import {
  getTrustBadgeConfig,
  hasDecisionContent,
} from "@/lib/decisionReport";
import { DayCard } from "@/components/DayCard";
import { LegReplacementDrawer } from "@/components/LegReplacementDrawer";
import type { LegDrawerProps } from "@/components/LegReplacementDrawer";
import { MoveActivityModal } from "@/components/ActivityModals";
import { ActivityAddDrawer } from "@/components/ActivityAddDrawer";
import { ActivitySuggestionsDrawer } from "@/components/ActivitySuggestionsDrawer";
import { analyzeTripInsights } from "@/lib/tripInsights";
import type { TripInsight } from "@/lib/tripInsights";
import {
  generateActivityId,
} from "@/lib/editableActivities";
import type { EditableActivity, ActivitySlot } from "@/lib/editableActivities";
import type { PdfExportInput } from "@/lib/pdfExport";
import { getSuggestions } from "@/lib/activitySuggestions";
import type { ActivitySuggestion } from "@/lib/activitySuggestions";
import type { DragState } from "@/lib/dragDrop";
import {
  generateItinerary,
  searchHotelsForStay,
  searchTransportForLeg,
  notifyWaitlist,
  type HotelOption,
  type TransportOption,
  type T1ItineraryResponse,
  type T1Alternative,
} from "@/lib/api";
import { useEditableItinerary } from "@/lib/useEditableItinerary";
import { DecisionMap } from "@/components/DecisionMap";
import { MobilityPreferences } from "@/components/MobilityPreferences";
import type { MobilityPref } from "@/components/MobilityPreferences";
import { getEditorialImage } from "@/data/editorialImages";

export const Route = createFileRoute("/itinerary")({
  component: ItineraryPage,
});

// ─── Feature flags ────────────────────────────────────────────────────────────
// Mirror of ENABLE_ACTIVITY_DND in DayCard.tsx. Both must be true to enable
// drag-and-drop. Keep in sync or extract to a shared constants file.
// TODO: re-enable once DnD is re-implemented with @dnd-kit (see DayCard.tsx).
const ENABLE_ACTIVITY_DND_ITINERARY = false;

// ─── Constants ────────────────────────────────────────────────────────────────

const TIPS = [
  "Preparando tu ruta",
  "Ajustando ritmo y experiencias",
  "Armando una propuesta clara",
  "Buscando la mejor combinación de ciudades",
  "Estimando costos de hospedaje",
];


const TYPE_COLORS: Record<string, string> = {
  budget:      "bg-slate-100 text-slate-600 border-slate-200",
  comfortable: "bg-blue-50 text-blue-700 border-blue-200",
  boutique:    "bg-purple-50 text-purple-700 border-purple-200",
  luxury:      "bg-amber-50 text-amber-700 border-amber-200",
};

const TRANSPORT_ICONS: Record<string, string> = {
  flight: "✈️",
  train:  "🚄",
  bus:    "🚌",
  ferry:  "⛴️",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function addDays(dateStr: string, n: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("es-CO", { day: "numeric", month: "short" });
}

function fmtDateRange(checkin: string, checkout: string): string {
  if (!checkin || !checkout) return "";
  const a = new Date(checkin + "T00:00:00");
  const b = new Date(checkout + "T00:00:00");
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  return `${a.toLocaleDateString("es-CO", opts)} – ${b.toLocaleDateString("es-CO", opts)}`;
}


// ─── Local type aliases ───────────────────────────────────────────────────────

/** Resolver metadata attached by the backend but not in the shared EngineItinerary type. */
interface ResolverInfo {
  resolved_name?: string;
  entity_type?: string;
  planning_tier?: string;
  candidate_pool?: string[];
  discarded_candidates?: string[];
  t1_parts_skipped?: string[];
  is_literal_multi_city?: boolean;
}

/** Full API response — extends EngineItinerary with extra backend fields. */
type ApiItinerary = EngineItinerary & {
  resolver?: ResolverInfo;
  trip_timeline?: TripTimeline;
  meta: EngineItinerary["meta"] & {
    resolved_destination?: string;
    solver_reasons?: string[] | null;
  };
};

// ─── Transport Panel ──────────────────────────────────────────────────────────
// TransportModalState is a local alias for the drawer's exported props type so
// the rest of this file continues to compile without changes.
type TransportModalState = LegDrawerProps;

// ─── Hotel Panel ──────────────────────────────────────────────────────────────

interface HotelModalState {
  stayIndex: number;
  cityIata: string;
  cityName: string;
  checkin: string;
  checkout: string;
  nights: number;
  travelers: number;
  currentPrice: number;
}

function HotelPanel({
  modal,
  onClose,
  onSelect,
}: {
  modal: HotelModalState;
  onClose: () => void;
  onSelect: (hotel: HotelOption, stayIndex: number) => void;
}) {
  const [hotels, setHotels] = useState<{ recommended: HotelOption[]; alternatives: HotelOption[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [maxPrice, setMaxPrice] = useState(1000);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(""); setHotels(null);
    searchHotelsForStay({
      city_iata: modal.cityIata,
      city_name: modal.cityName,
      checkin:   modal.checkin || undefined,
      checkout:  modal.checkout || undefined,
      nights:    modal.nights,
      travelers: modal.travelers,
    }).then(res => {
      if (res.success) {
        setHotels({ recommended: res.recommended, alternatives: res.alternatives });
        // Set max price to 2x the luxury option or 500, whichever is bigger
        const allPrices = [...res.recommended, ...res.alternatives].map(h => h.price_per_night);
        setMaxPrice(Math.max(500, Math.ceil(Math.max(...allPrices) * 1.2 / 50) * 50));
      } else {
        setError("No pudimos cargar opciones de hoteles.");
      }
      setLoading(false);
    }).catch(() => {
      setError("Error al cargar hoteles.");
      setLoading(false);
    });
  }, [modal.cityIata, modal.cityName, modal.checkin, modal.checkout, modal.nights, modal.travelers]);

  const allHotels = hotels ? [...hotels.recommended, ...hotels.alternatives] : [];
  const filtered = allHotels.filter(h =>
    h.price_per_night <= maxPrice &&
    (!typeFilter || h.type === typeFilter)
  );
  const recommended = filtered.filter(h => h.type === "comfortable");
  const alternatives = filtered.filter(h => h.type !== "comfortable");

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-card border-l border-border z-50 flex flex-col shadow-2xl"
      >
        {/* ── Panel header ─────────────────────────────────────────────── */}
        <div className="p-5 border-b border-border shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-bold text-lg">Hoteles en {modal.cityName}</h2>
              <p className="text-muted-foreground text-sm mt-0.5">
                {modal.checkin && modal.checkout
                  ? `${fmtDateRange(modal.checkin, modal.checkout)} · `
                  : ""}
                {modal.nights} noche{modal.nights !== 1 ? "s" : ""} · {modal.travelers} viajero{modal.travelers !== 1 ? "s" : ""}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-accent flex items-center justify-center text-muted-foreground transition-colors"
            >
              ✕
            </button>
          </div>

          {/* ── Filters ────────────────────────────────────────────────── */}
          <div className="mt-4 space-y-3">
            {/* Hotel type chips */}
            <div className="flex gap-2 flex-wrap">
              {[
                { id: null,          label: "Todos" },
                { id: "budget",      label: "Económico" },
                { id: "comfortable", label: "Confortable" },
                { id: "boutique",    label: "Boutique" },
                { id: "luxury",      label: "Lujo" },
              ].map(t => (
                <button
                  key={String(t.id)}
                  onClick={() => setTypeFilter(t.id)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                    typeFilter === t.id
                      ? "border-forest/35 bg-forest/[0.09] text-forest"
                      : "border-border text-muted-foreground hover:border-border/80"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Price range */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">
                Precio máx: <span className="text-foreground font-semibold">${maxPrice}/noche</span>
              </label>
              <input
                type="range" min={30} max={1000} step={10} value={maxPrice}
                onChange={e => setMaxPrice(Number(e.target.value))}
                className="w-full accent-forest"
              />
            </div>
          </div>
        </div>

        {/* ── Panel body ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-5">
              <div className="w-40 h-px bg-border/30 relative overflow-hidden rounded-full">
                <motion.div
                  className="absolute inset-y-0 w-16 rounded-full"
                  style={{ background: "oklch(0.298 0.058 167 / 0.45)" }}
                  animate={{ x: ["-100%", "300%"] }}
                  transition={{ duration: 1.4, ease: "easeInOut", repeat: Infinity }}
                />
              </div>
              <p className="text-muted-foreground/60 text-sm">Buscando hoteles...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{error}</p>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-sm">
                No hay hoteles con los filtros actuales. Intenta ampliar el rango de precio.
              </p>
            </div>
          )}

          {/* Recommended */}
          {recommended.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-forest/55 mb-3">
                Selección Sift Trip
              </h3>
              <div className="space-y-3">
                {recommended.map(hotel => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    nights={modal.nights}
                    currentPrice={modal.currentPrice}
                    onSelect={() => onSelect(hotel, modal.stayIndex)}
                    highlight
                  />
                ))}
              </div>
            </div>
          )}

          {/* Alternatives */}
          {alternatives.length > 0 && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                Otras opciones
              </h3>
              <div className="space-y-3">
                {alternatives.map(hotel => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    nights={modal.nights}
                    currentPrice={modal.currentPrice}
                    onSelect={() => onSelect(hotel, modal.stayIndex)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ─── Hotel Card ───────────────────────────────────────────────────────────────

function HotelCard({
  hotel,
  nights,
  currentPrice: _currentPrice,
  onSelect,
  highlight = false,
}: {
  hotel: HotelOption;
  nights: number;
  currentPrice: number;
  onSelect: () => void;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-2xl border p-4 transition-all ${
      highlight
        ? "border-forest/28 bg-forest/[0.04] shadow-[0_2px_12px_oklch(0.298_0.058_167_/_0.07)]"
        : "border-border bg-card/60 hover:border-border/80"
    }`}>
      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm leading-tight">{hotel.name}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TYPE_COLORS[hotel.type]}`}>
              {hotel.type_label}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            📍 {hotel.neighborhood}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold text-foreground/80 leading-none">
            ${hotel.price_per_night}
          </div>
          <div className="text-xs text-muted-foreground">/noche</div>
        </div>
      </div>

      {/* Stars + rating */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs text-amber-500">
          {"★".repeat(hotel.stars)}{"☆".repeat(5 - hotel.stars)}
        </span>
        <span className="text-xs font-semibold">{hotel.rating}/10</span>
      </div>

      {/* Total */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-muted-foreground">
          ${hotel.total_price.toLocaleString()} total ({nights} noches)
        </span>
      </div>

      {/* Amenities */}
      <div className="flex flex-wrap gap-1 mb-3">
        {hotel.amenities.slice(0, 4).map(a => (
          <span key={a} className="text-[10px] bg-accent text-muted-foreground px-2 py-0.5 rounded-full">
            {a}
          </span>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={onSelect}
        className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all ${
          highlight
            ? "bg-forest/[0.09] text-forest border border-forest/25 hover:bg-forest/[0.14] hover:border-forest/38"
            : "border border-border text-muted-foreground hover:border-forest/30 hover:text-forest/75 hover:bg-forest/[0.04]"
        }`}
      >
        Elegir esta opción
      </button>
    </div>
  );
}

// TransportCard and TransportPanel removed — replaced by LegReplacementDrawer

// ─── Coming Soon Screen ───────────────────────────────────────────────────────

const CONTINENT_NAMES: Record<string, string> = {
  europe:        "Europa",
  asia_pacific:  "Asia y Pacífico",
  north_america: "Norteamérica",
  latin_america: "Latinoamérica",
  middle_east:   "Medio Oriente",
  africa:        "África",
  oceania:       "Oceanía",
};

const CONTINENT_EMOJIS: Record<string, string> = {
  europe:        "🏰",
  asia_pacific:  "🏯",
  north_america: "🗽",
  latin_america: "🌺",
  middle_east:   "🕌",
  africa:        "🌍",
  oceania:       "🐨",
};

function ComingSoonScreen({
  t1,
  origin,
  onExploreAlternative,
}: {
  t1: T1ItineraryResponse;
  origin: string;
  onExploreAlternative: (alt: T1Alternative) => void;
}) {
  const [email, setEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const continentLabel = t1.continent_id ? (CONTINENT_NAMES[t1.continent_id] || t1.continent_id) : null;
  const continentEmoji = t1.continent_id ? (CONTINENT_EMOJIS[t1.continent_id] || "🌍") : "🌍";

  // Partition alternatives
  const sameCountry  = t1.suggested_alternatives.filter(a => a.type === "same_country_city");
  const countryAlt   = t1.suggested_alternatives.find(a => a.type === "country");
  const continentCities = t1.suggested_alternatives.filter(a => a.type === "continent_city");
  const continentAlt = t1.suggested_alternatives.find(a => a.type === "continent");

  const handleNotify = async () => {
    if (!email || notifying) return;
    setNotifying(true);
    try {
      await notifyWaitlist({
        destination:    t1.matched_name,
        country_iso2:   t1.country_iso2 || undefined,
        continent_id:   t1.continent_id || undefined,
        email,
      });
      setNotified(true);
      toast.success("¡Listo! Te avisaremos cuando esté disponible.");
    } catch {
      toast.error("Error al registrar. Intenta de nuevo.");
    }
    setNotifying(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        {/* Header card */}
        <div className="rhea-card-static p-8 text-center mb-6 border-gold/20"
          style={{ background: "linear-gradient(135deg, oklch(0.21 0.025 50), oklch(0.185 0.020 45))" }}>
          <div className="text-5xl mb-4">🔭</div>
          <h1 className="text-2xl font-bold mb-2">Sift Trip reconoce este destino</h1>
          <p className="text-gold font-semibold text-lg mb-1">{t1.matched_name}</p>
          <p className="text-muted-foreground text-sm mb-5">{t1.message_detail}</p>

          {/* Notify me */}
          {!notified ? (
            <div className="flex gap-2 max-w-sm mx-auto">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleNotify()}
                placeholder="tu@email.com"
                className="flex-1 px-3 py-2 rounded-xl bg-input border border-border text-sm focus:outline-none focus:border-gold/50 transition-colors"
              />
              <button
                onClick={handleNotify}
                disabled={notifying || !email}
                className="btn-gold text-sm px-4 whitespace-nowrap disabled:opacity-50"
              >
                {notifying ? "..." : "Notifícame"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-green-700 text-sm font-semibold">
              <span>✓</span>
              <span>¡Registrado! Te avisaremos cuando {t1.matched_name} esté listo.</span>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground/50 mt-2">Sin spam. Solo un aviso cuando esté listo.</p>
        </div>

        {/* Alternatives section */}
        <div className="space-y-5">

          {/* Same-country cities */}
          {sameCountry.length > 0 && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-1">
                🏙️ Ciudades disponibles en el mismo país
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {sameCountry.map(alt => (
                  <AlternativeCard key={alt.destination} alt={alt} origin={origin} onExplore={onExploreAlternative} />
                ))}
              </div>
            </div>
          )}

          {/* Country option */}
          {countryAlt && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-1">
                🗺️ Explorar el país completo
              </h2>
              <AlternativeCard alt={countryAlt} origin={origin} onExplore={onExploreAlternative} wide />
            </div>
          )}

          {/* Continent cities */}
          {continentCities.length > 0 && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-1">
                {continentEmoji} Destinos populares en {continentLabel || "la región"}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {continentCities.map(alt => (
                  <AlternativeCard key={alt.destination} alt={alt} origin={origin} onExplore={onExploreAlternative} />
                ))}
              </div>
            </div>
          )}

          {/* Continent option */}
          {continentAlt && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 px-1">
                {continentEmoji} Explorar {continentLabel || "la región"} completa
              </h2>
              <AlternativeCard alt={continentAlt} origin={origin} onExplore={onExploreAlternative} wide />
            </div>
          )}
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors">
            ← Cambiar búsqueda
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

function AlternativeCard({
  alt,
  origin,
  onExplore,
  wide = false,
}: {
  alt: T1Alternative;
  origin: string;
  onExplore: (alt: T1Alternative) => void;
  wide?: boolean;
}) {
  const isT3 = alt.planning_tier === "curated";
  const isCountryOrContinent = alt.entity_type === "country" || alt.entity_type === "continent" || alt.entity_type === "region";

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className={`rhea-card-static p-4 flex ${wide ? "items-center justify-between gap-4" : "flex-col gap-3"} border-border/60 hover:border-gold/30 transition-all cursor-pointer`}
      onClick={() => onExplore(alt)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-semibold text-sm">{alt.label}</span>
          {isT3 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-gold/10 text-gold border-gold/30">
              ✦ Curado
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{alt.reason}</p>
      </div>
      <button
        className={`${wide ? "shrink-0" : "w-full"} btn-warm text-sm`}
        onClick={e => { e.stopPropagation(); onExplore(alt); }}
      >
        {isCountryOrContinent ? "Explorar región" : "Planificar viaje"} →
      </button>
    </motion.div>
  );
}

// ─── Trust Badge ─────────────────────────────────────────────────────────────
// Compact inline badge shown in the itinerary header stats row.

function TrustBadge({ report }: { report: DecisionReport | undefined | null }) {
  if (!report) return null;
  const cfg = getTrustBadgeConfig(report.trust_level);
  return (
    <span
      className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-semibold border ${cfg.bgColor} ${cfg.borderColor} ${cfg.scoreColor} cursor-default`}
      title={report.explanation}
    >
      <span className={`w-2 h-2 rounded-full ${cfg.dotColor} shrink-0`} />
      {cfg.label}
    </span>
  );
}

// ─── Friction Badge ───────────────────────────────────────────────────────────
// Surfaces itinerary livability and route quality calmly below the header.
// Never exposes raw scores, metrics, or debug data.

const FRICTION_CONFIG: Record<
  TravelFrictionReport["friction_level"],
  { label: string; icon: string; pill: string }
> = {
  low:      { label: "Ritmo cómodo",        icon: "✓", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  moderate: { label: "Viaje intenso",        icon: "△", pill: "bg-amber-50   text-amber-700   border-amber-200"   },
  high:     { label: "Itinerario exigente",  icon: "⚠", pill: "bg-red-50     text-red-600     border-red-200"     },
};

const ROUTE_LOGIC_HINT: Record<
  Exclude<TravelFrictionReport["route_logic_level"], "good">,
  string
> = {
  questionable: "La ruta revisita destinos anteriores",
  poor:         "La ruta puede ser poco eficiente",
};

function FrictionBadge({ report }: { report: TravelFrictionReport | undefined | null }) {
  if (!report) return null;

  const cfg        = FRICTION_CONFIG[report.friction_level];
  const routeHint  = report.route_logic_level !== "good"
    ? ROUTE_LOGIC_HINT[report.route_logic_level as keyof typeof ROUTE_LOGIC_HINT]
    : null;

  return (
    <div className="mt-3 space-y-1.5">
      {/* Row: pace pill + optional route-logic hint */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.pill}`}
        >
          <span>{cfg.icon}</span>
          <span>{cfg.label}</span>
        </span>
        {routeHint && (
          <span className="text-xs text-muted-foreground/70 italic">
            · {routeHint}
          </span>
        )}
      </div>
      {/* One-line human summary — shown only when available */}
      {report.human_summary && (
        <p className="text-xs text-muted-foreground/60 leading-relaxed">
          {report.human_summary}
        </p>
      )}
    </div>
  );
}

// ─── Decision Panel ───────────────────────────────────────────────────────────
// Collapsible panel shown below the summary when trust_level is medium or low,
// or when the user opens "Por qué Sift Trip recomienda esta ruta".

function DecisionPanel({ report }: { report: DecisionReport | undefined | null }) {
  const [expanded, setExpanded] = useState(false);

  if (!report) return null;

  // Only surface the panel when there's something worth showing the user
  const hasContent = hasDecisionContent(report);
  if (!hasContent) return null;

  return (
    <div className="mt-3">
      {/* Subtle text link — not a prominent button */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="text-[11px] text-muted-foreground/50 hover:text-muted-foreground/80 transition-colors underline-offset-2 hover:underline"
      >
        {expanded ? "Ocultar detalles del itinerario" : "Ver notas sobre este itinerario"}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-xl border border-border/40 bg-accent/20 p-4 space-y-3">

              {/* Explanation — kept but de-emphasised */}
              <p className="text-xs text-muted-foreground/70 leading-relaxed">{report.explanation}</p>

              {/* Hard warnings — only shown when present */}
              {report.hard_warnings.length > 0 && (
                <div className="space-y-1.5">
                  {report.hard_warnings.map((w, i) => (
                    <div key={i} className="flex gap-2 text-xs text-red-700/80 bg-red-50 border border-red-100/60 rounded-lg px-3 py-2">
                      <span className="shrink-0 mt-0.5">⚠</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Soft warnings */}
              {report.soft_warnings.length > 0 && (
                <div className="space-y-1.5">
                  {report.soft_warnings.map((w, i) => (
                    <div key={i} className="flex gap-2 text-xs text-amber-800/70 bg-amber-50/60 border border-amber-100/50 rounded-lg px-3 py-2">
                      <span className="shrink-0 mt-0.5">·</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations */}
              {report.recommendations.length > 0 && (
                <div className="space-y-1.5">
                  {report.recommendations.map((r, i) => (
                    <div key={i} className="flex gap-2 text-xs text-foreground/60 bg-accent/30 border border-border/30 rounded-lg px-3 py-2">
                      <span className="shrink-0 mt-0.5 text-gold/70">→</span>
                      <span>{r}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ─── Modification Intent Engine ───────────────────────────────────────────────
// Lightweight heuristic parser — no LLM call. Matches common modification
// patterns and returns a calm one-sentence reasoning + optional new params.

interface ModifyState {
  reasoning: string;
  /** Short editorial note shown after the rearranged itinerary loads. */
  summary?: string;
  newParams?: Record<string, string>;
}

/** Contextual quick-edit chips — each carries a free-text intent string that
 *  feeds parseModifyIntent, and a post-rearrangement summary sentence. */
const QUICK_CHIPS: Array<{ label: string; intent: string; summary: string }> = [
  {
    label:   "Más tranquilo",
    intent:  "ritmo más tranquilo",
    summary: "Voy a organizar el viaje con más días por ciudad y menos cambios de lugar.",
  },
  {
    label:   "Más gastronomía",
    intent:  "más gastronomía",
    summary: "Voy a priorizar experiencias gastronómicas, mercados locales y restaurantes con historia.",
  },
  {
    label:   "Menos caminata",
    intent:  "menos caminata",
    summary: "Voy a equilibrar las actividades para reducir los recorridos a pie.",
  },
  {
    label:   "Más naturaleza",
    intent:  "más naturaleza",
    summary: "Voy a incorporar parques, reservas y espacios al aire libre como pausa natural.",
  },
  {
    label:   "Menos traslados",
    intent:  "menos traslados",
    summary: "Voy a reducir los traslados largos y concentrar mejor la ruta.",
  },
  {
    label:   "Mejor para niños",
    intent:  "mejor para niños",
    summary: "Voy a ajustar el ritmo y las actividades para que el viaje funcione bien para toda la familia.",
  },
];

/** Itinerary context passed into the intent parser for city-aware responses. */
interface ItineraryContext {
  /** City names in visit order, e.g. ["Lisboa", "Porto", "Madrid"] */
  cities:       string[];
  firstCity:    string;
  lastCity:     string;
  totalDays:    number;
  totalNights:  number;
  mood:         string;
  mode:         string;
}

function parseModifyIntent(text: string, ctx?: ItineraryContext): ModifyState {
  const t = text.toLowerCase();

  // ── Context helpers ───────────────────────────────────────────────────────
  // Find a city from the current itinerary mentioned in the user's text
  const mentionedCity = ctx?.cities.find(city =>
    t.includes(city.toLowerCase())
  ) ?? null;
  const lastCity  = ctx?.lastCity  ?? "";
  const firstCity = ctx?.firstCity ?? "";

  // ── City-specific pace ────────────────────────────────────────────────────
  // "Quiero algo más tranquilo en Lisboa" / "más tranquilo en Porto"
  if (mentionedCity && /tranquilo|tranquila|calma|slow|relax|lento|descanso|respirar/i.test(t)) {
    return {
      reasoning: `Voy a reorganizar ${mentionedCity} con tardes más tranquilas y más tiempo libre entre actividades.`,
      summary:   `Voy a reorganizar ${mentionedCity} con un ritmo más calmado.`,
      newParams: { mode: "smooth" },
    };
  }

  // ── More time in a specific city ──────────────────────────────────────────
  // "Más tiempo en Lisboa" / "Quedarme más en Porto" / "Un día más en Madrid"
  if (mentionedCity && /más tiempo|quedar.*más|stay longer|more time|extra.*día|un día más|día más/i.test(t)) {
    return {
      reasoning: `Voy a añadir más tiempo en ${mentionedCity} para que puedas explorarla con más calma.`,
      summary:   `Voy a ampliar la estadía en ${mentionedCity}.`,
      newParams: {},
    };
  }

  // ── City-specific gastronomy ──────────────────────────────────────────────
  // "Más gastronomía en Lisboa" / "quiero comer bien en Porto"
  if (mentionedCity && /gastr|comida|restaurante|cena|food|mercado|vino|wine/i.test(t)) {
    return {
      reasoning: `Voy a priorizar mercados locales, restaurantes con historia y maridajes regionales en ${mentionedCity}.`,
      summary:   `Voy a priorizar experiencias gastronómicas en ${mentionedCity}.`,
      newParams: { mood: "gastronomia" },
    };
  }

  // ── City-specific nature ──────────────────────────────────────────────────
  // "Prefiero naturaleza aquí" / "Prefiero naturaleza en Lisboa"
  if (mentionedCity && /naturaleza|nature|aire libre|outdoor|parque|bosque|verde/i.test(t)) {
    return {
      reasoning: `Voy a incorporar espacios naturales y actividades al aire libre en ${mentionedCity}.`,
      summary:   `Voy a priorizar naturaleza y espacios abiertos en ${mentionedCity}.`,
      newParams: { mood: "naturaleza" },
    };
  }

  // ── Romantic / special evening ────────────────────────────────────────────
  // "Algo romántico para la última noche" / "Una noche especial"
  if (/romántico|romantic|especial.*noche|noche.*especial|última.*noche|noche.*última|last.*night/i.test(t)) {
    const cityRef = lastCity || mentionedCity || "";
    return {
      reasoning: cityRef
        ? `Puedo convertir la última noche en ${cityRef} en una experiencia más especial — cena con ambiente, opciones boutique.`
        : "Puedo reservar la última noche para algo más especial — cena con ambiente y opciones boutique.",
      summary:   "Voy a hacer de la última noche una experiencia más especial.",
      newParams: { mood: "romantico" },
    };
  }

  // ── Special night on a day of week ───────────────────────────────────────
  // "Quiero una noche especial el viernes" / "algo especial el sábado"
  if (/(?:noche|noche especial|algo especial).*(?:viernes|sábado|domingo|lunes|martes|miércoles|jueves)|(?:viernes|sábado|domingo|lunes|martes|miércoles|jueves).*(?:noche|especial)/i.test(t)) {
    return {
      reasoning: "Puedo reservar esa noche para algo más especial — una cena con historia o un lugar con buena atmósfera.",
      summary:   "Voy a organizar una noche más especial en esa fecha.",
      newParams: { mood: "gastronomia" },
    };
  }

  // ── No early starts ────────────────────────────────────────────────────────
  // "No quiero madrugar ese día" / "Sin madrugones" / "salida tarde"
  if (/madrugar|madrugón|madrugones|early.*start|early.*morning|mañana.*temprano|no.*temprano|salida.*tarde/i.test(t)) {
    return {
      reasoning: "Voy a dejar las mañanas más libres — salidas tardías y actividades a partir del mediodía.",
      summary:   "Voy a organizar el día para que no haya que madrugar.",
      newParams: { mode: "smooth" },
    };
  }

  // ── Less hotel changes ────────────────────────────────────────────────────
  // "Menos cambios de hotel" / "No quiero cambiar de hotel tanto"
  if (/cambios.*hotel|hotel.*cambio|cambia.*hotel|hotel.*traslado|menos.*hotel.*cambio|no.*cambiar.*hotel/i.test(t)) {
    return {
      reasoning: "Voy a concentrar la ruta para reducir los cambios de hotel — estadías más largas, logística más simple.",
      summary:   "Voy a reducir los cambios de hotel para un viaje más fluido.",
      newParams: { mode: "smooth" },
    };
  }

  // ── City swap ─────────────────────────────────────────────────────────────
  const swapRx = /(?:prefer(?:o|ir)?|swap|cambiar?|en lugar de|instead of)\s+([\wÀ-ɏ\s]+?)\s+(?:instead of|en lugar de|en vez de|por|over|before)\s+([\wÀ-ɏ\s]+)/i;
  const swapMatch = text.match(swapRx);
  if (swapMatch) {
    const incoming = swapMatch[1].trim();
    const outgoing = swapMatch[2].trim();
    return {
      reasoning: `Voy a reemplazar ${outgoing} por ${incoming} — haría la ruta más coherente y simplificaría la logística.`,
      summary:   `Voy a cambiar ${outgoing} por ${incoming} en la ruta.`,
      newParams: { destination: incoming },
    };
  }

  // ── Pace / slower (generic — no city mentioned) ──────────────────────────
  if (/slower|más lento|tranquilo|tranquila|calma|menos ritmo|slow\b/i.test(t)) {
    const cityRef = firstCity ? ` — empezando por ${firstCity}` : "";
    return {
      reasoning: `Voy a reorganizar el viaje con más días en menos ciudades${cityRef}, menos traslados y más tiempo para explorar.`,
      summary:   "Voy a reorganizar el viaje con un ritmo más tranquilo.",
      newParams: { mode: "smooth" },
    };
  }

  // ── Fewer hotel changes (generic) ────────────────────────────────────────
  if (/fewer hotel|menos.*hotel|reduce.*hotel|menos cambios|less hotel/i.test(t)) {
    return {
      reasoning: "Voy a reducir los cambios de hotel para estadías más largas en cada ciudad y logística más simple.",
      summary:   "Voy a reducir los cambios de hotel para un viaje más fluido.",
      newParams: { mode: "smooth" },
    };
  }

  // ── Less moving around ────────────────────────────────────────────────────
  if (/less moving|menos desplazamien|fewer transfers|menos traslados|less travel/i.test(t)) {
    return {
      reasoning: "Voy a concentrar la ruta — más días en cada parada, menos tiempo en tránsito.",
      summary:   "Voy a reducir los traslados y concentrar mejor la ruta.",
      newParams: { mode: "smooth" },
    };
  }

  // ── Food / gastronomy (generic) ───────────────────────────────────────────
  if (/food|gastr|comida|restaurante|cena|wine|vino|market|mercado/i.test(t)) {
    return {
      reasoning: "Voy a priorizar mercados locales, restaurantes con historia y maridajes regionales en cada ciudad.",
      summary:   "Voy a priorizar experiencias gastronómicas, mercados locales y restaurantes con historia.",
      newParams: { mood: "gastronomia" },
    };
  }

  // ── Nature (generic) ─────────────────────────────────────────────────────
  if (/nature|naturaleza|hiking|trekking|monta[nñ]a|bosque|outdoor|park|parque|aire libre/i.test(t)) {
    return {
      reasoning: "Voy a incorporar parques, reservas y espacios al aire libre como pausas naturales a lo largo de la ruta.",
      summary:   "Voy a incorporar naturaleza y espacios al aire libre como pausa natural.",
      newParams: { mood: "naturaleza" },
    };
  }

  // ── Less walking ──────────────────────────────────────────────────────────
  if (/caminata|menos.*caminando|menos.*pie|walk|pedestr|no.*caminar/i.test(t)) {
    return {
      reasoning: "Voy a equilibrar el día con menos recorridos a pie — museos, mercados y gastronomía al alcance sin grandes caminatas.",
      summary:   "Voy a equilibrar las actividades para reducir los recorridos a pie.",
      newParams: { mood: "cultura" },
    };
  }

  // ── Culture / museums ────────────────────────────────────────────────────
  if (/cultur|museum|museo|histor|art|arte|arquitectura/i.test(t)) {
    return {
      reasoning: "Voy a priorizar museos, arquitectura y barrios históricos en cada destino.",
      summary:   "Voy a priorizar cultura, museos y arquitectura en el itinerario.",
      newParams: { mood: "cultura" },
    };
  }

  // ── Family / kids ─────────────────────────────────────────────────────────
  if (/ni[ñn]o|familia|child|kid|family/i.test(t)) {
    return {
      reasoning: "Voy a ajustar el ritmo y las actividades para que el viaje funcione bien para toda la familia.",
      summary:   "Voy a ajustar el ritmo y las actividades para que el viaje funcione bien para toda la familia.",
      newParams: { mode: "smooth" },
    };
  }

  // ── Budget / cheaper ─────────────────────────────────────────────────────
  if (/cheap|barato|econ[oó]mico|budget|ahorra|less expensive/i.test(t)) {
    return {
      reasoning: "Voy a ajustar el nivel de hospedaje y priorizar transporte terrestre donde sea posible.",
      summary:   "Voy a optimizar el presupuesto ajustando hospedaje y transporte.",
      newParams: { mode: "budget" },
    };
  }

  // ── Premium / luxury ─────────────────────────────────────────────────────
  if (/premium|lujo|luxury|boutique|high.?end/i.test(t)) {
    return {
      reasoning: "Voy a elevar el nivel de hospedaje y priorizar opciones de transporte directas y cómodas.",
      summary:   "Voy a elevar el nivel del viaje con opciones más premium.",
      newParams: { mode: "experience" },
    };
  }

  // ── Default ───────────────────────────────────────────────────────────────
  return {
    reasoning: "Voy a ajustar el itinerario según esta preferencia.",
    summary:   "Voy a aplicar este ajuste al itinerario.",
  };
}

// ─── Main ─────────────────────────────────────────────────────────────────────

// TanStack Router auto-JSON-parses every URL search param with JSON.parse.
// Simple strings that are not valid JSON stay as strings; valid JSON (arrays,
// numbers, booleans) come back as their parsed type. We must handle both the
// "already parsed" case (array/object) and the legacy "raw string" case so
// that double-parsing does not silently return null and trigger auto-mode.
function parseUrlJsonParam<T>(v: unknown): T | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "object") return v as T;  // already parsed by TanStack Router
  if (typeof v === "string") { try { return JSON.parse(v) as T; } catch { return null; } }
  return null;
}

function ItineraryPage() {
  // Cast to unknown first so we can safely read already-parsed values (arrays,
  // booleans) that TanStack Router converted before we get them.
  const raw = Route.useSearch() as Record<string, unknown>;
  const origin      = String(raw.origin    || "BOG");
  const region      = String(raw.region    || "Europa");
  const destination = raw.destination ? String(raw.destination) : undefined;
  const budget    = Number(raw.budget)    || 3000;
  const days      = Number(raw.days)      || 10;
  const travelers = Number(raw.travelers) || 2;
  const mood      = String(raw.mood   || "cultura");
  const mode      = String(raw.mode   || "smooth");
  const depDate        = raw.departure_date ? String(raw.departure_date) : undefined;
  // Temporal Planning Engine v1 — "YYYY-MM" passed as travel_month from index.tsx
  const travelMonthYm  = raw.travel_month ? String(raw.travel_month) : undefined;

  const fixedCities    = parseUrlJsonParam<import("@/lib/api").FixedCity[]>(raw.fixed_cities);

  // Sprint 2 — Route Intelligence contract from index.tsx buildItinerarySearchParams
  const routeCities    = parseUrlJsonParam<unknown[]>(raw.route_cities);
  const openConstraints = parseUrlJsonParam<unknown[]>(raw.open_constraints);
  // JSON.parse("true") = boolean true, so check both parsed and string form
  const multiCity = raw.multi_city === true || raw.multi_city === "true";

  // Mobility preferences — comma-separated list from URL param.
  // "prefer_train,cheapest" is not valid JSON so TanStack Router keeps it as string.
  const _rawMobilityPref = raw.mobility_preference;
  const mobilityPrefs: MobilityPref[] = _rawMobilityPref
    ? (Array.isArray(_rawMobilityPref)
        ? (_rawMobilityPref as MobilityPref[])
        : (String(_rawMobilityPref).split(",") as MobilityPref[]))
    : [];

  const [result, setResult]         = useState<ApiItinerary | null>(null);
  const [t1Result, setT1Result]     = useState<T1ItineraryResponse | null>(null);
  const [loading, setLoading]       = useState(true);
  // ── Modify bar ────────────────────────────────────────────────────────────
  const [modifyText, setModifyText]           = useState("");
  const [modifyReasoning, setModifyReasoning] = useState<ModifyState | null>(null);
  const [modifyOpen, setModifyOpen]           = useState(false);
  /** Label of the currently selected quick chip, or null when free-text is used. */
  const [activeChip, setActiveChip]           = useState<string | null>(null);
  /** Dismissal flag for the post-rearrangement editorial summary note. */
  const [noteDismissed, setNoteDismissed]     = useState(false);
  // ── Map ───────────────────────────────────────────────────────────────────
  const [mapExpanded, setMapExpanded]         = useState(false);
  // ── Hero image ────────────────────────────────────────────────────────────
  // Tracks whether the curated hero image failed to load — triggers typographic fallback.
  const [heroImageError, setHeroImageError]   = useState(false);
  const [error, setError]           = useState("");
  const [tipIdx, setTipIdx]         = useState(0);
  // activeDay removed — DayCard shows content inline (no accordion)
  const [pdfLoading, setPdfLoading] = useState(false);
  // ── Editable itinerary state (single source of truth post-fetch) ──────────
  const {
    state:            editableState,
    stays:            editedStays,
    legs:             editedLegs,
    activities:       editedActivities,
    timelineDays:     editableTimelineDays,
    tripTimeline:     editableTripTimeline,
    loadingKeys:      mutationLoading,
    historyLength:    mutationHistoryLength,
    init:             initEditable,
    mutate:           handleMutation,
    undo:             handleUndo,
    setStays:         setEditedStays,
    setLegs:          setEditedLegs,
    setActivities:    setEditedActivities,
  } = useEditableItinerary({
    mobilityPreference: mobilityPrefs.length ? mobilityPrefs.join(",") : undefined,
  });
  const [hotelModal, setHotelModal]               = useState<HotelModalState | null>(null);
  const [transportModal, setTransportModal]       = useState<TransportModalState | null>(null);
  // Activity modal state
  const [addActivityModal, setAddActivityModal]       = useState<{ dayNumber: number; city: string } | null>(null);
  const [moveActivityModal, setMoveActivityModal]     = useState<string | null>(null); // activityId
  const [changeActivityModal, setChangeActivityModal] = useState<string | null>(null); // activityId
  // Drag-and-drop state — null when no drag is in flight
  const [dragState, setDragState] = useState<DragState | null>(null);
  // Map interaction state
  const [hoveredLegIndex, setHoveredLegIndex]   = useState<number | null>(null);
  const [selectedCityIata, setSelectedCityIata] = useState<string | null>(null);
  // Insight dismissal — in-memory only (resets on page reload, by design)
  const [dismissedInsightIds, setDismissedInsightIds] = useState<Set<string>>(new Set());
  const handleDismissInsight = (id: string) =>
    setDismissedInsightIds(prev => new Set([...prev, id]));

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setTipIdx((p) => (p + 1) % TIPS.length), 2800);
    return () => clearInterval(t);
  }, [loading]);

  const fetchItinerary = async () => {
    setLoading(true); setError(""); setT1Result(null);
    setModifyReasoning(null);
    console.log("[RHEA itinerary] route_cities from URL:", routeCities);
    console.log("[RHEA itinerary] open_constraints from URL:", openConstraints);
    console.log("[RHEA itinerary] multi_city:", multiCity);
    try {
      const res = await generateItinerary({
        origin,
        region: region || "Custom",
        ...(destination ? { destination } : {}),
        budget_usd: budget,
        days,
        travelers,
        mood,
        mode: mode as ItineraryMode,
        departure_date: depDate,
        ...(fixedCities ? { fixed_cities: fixedCities } : {}),
        ...(routeCities?.length      ? { route_cities: routeCities }         : {}),
        ...(openConstraints?.length  ? { open_constraints: openConstraints } : {}),
        ...(multiCity                ? { multi_city: true }                  : {}),
        ...(_rawMobilityPref         ? { mobility_preference: String(_rawMobilityPref) } : {}),
        ...(travelMonthYm            ? { travel_month_ym: travelMonthYm }    : {}),
      });
      if (!res.success && res.planning_tier === "resolver_only") {
        setT1Result(res as T1ItineraryResponse);
        setLoading(false);
        return;
      }
      if (!res.success) throw new Error((res as { error?: string }).error || "Error del motor");
      setResult(res as ApiItinerary);

      // Build initial activities using city_contexts for POI enrichment
      const initCityContextsMap: Record<string, import("@/lib/types").CityContent> = {};
      for (const ctx of res.city_contexts ?? []) {
        initCityContextsMap[ctx.city] = ctx;
      }
      // [StateNormalization] Hand off canonical as single source of truth.
      // Activities are embedded in timeline_days[n].activities by the backend.
      // Prefer canonical_itinerary when backend returned it; fall back to flat arrays.
      const canonical: CanonicalItinerary = res.canonical_itinerary ?? {
        stays:            res.stays as EngineStay[],
        legs:             res.legs  as EngineLeg[],
        return_leg:       null,
        timeline_days:    res.days.map((d: Record<string, unknown>, i: number) => ({
          ...d,
          day_id:     `d-${d.day ?? i + 1}`,
          activities: [],
        })),
        trip_timeline:    res.trip_timeline ?? null,
        temporal_qa:      res.trip_timeline?.temporal_qa,
        mutation_summary: null,
        mutation_events:  [],
      };
      console.log(`[StateNormalization] fetchItinerary: canonical_itinerary=${res.canonical_itinerary ? "from backend" : "fallback"} stays=${canonical.stays.length} timeline_days=${canonical.timeline_days.length}`);
      initEditable(canonical);

    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Sift Trip tuvo un problema. ¿Intentamos de nuevo?");
    }
    setLoading(false);
  };

  const handleExploreAlternative = (alt: T1Alternative) => {
    const params = new URLSearchParams({
      origin,
      region:      alt.continent_id || region,
      destination: alt.destination,
      budget:      String(budget),
      days:        String(days),
      travelers:   String(travelers),
      mood,
      mode,
      ...(depDate ? { departure_date: depDate } : {}),
    });
    window.location.href = `/itinerary?${params.toString()}`;
  };

  useEffect(() => { fetchItinerary(); }, []); // eslint-disable-line

  // ── Modify bar ────────────────────────────────────────────────────────────

  const handleModify = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const state = parseModifyIntent(trimmed, modifyContext);
    setModifyReasoning(state);
    setModifyText(trimmed);
  };

  /** Clicking a quick chip triggers intent parsing immediately and opens the panel. */
  const handleChipClick = (chip: typeof QUICK_CHIPS[number]) => {
    setActiveChip(chip.label);
    const state = parseModifyIntent(chip.intent, modifyContext);
    // Chip's pre-written summary takes precedence; context-aware reasoning fills the panel
    setModifyReasoning({ ...state, summary: chip.summary });
    setModifyText(chip.intent);
    setModifyOpen(true);
  };

  const handleRegenerate = () => {
    const extra = modifyReasoning?.newParams ?? {};
    const params = new URLSearchParams({
      origin,
      region,
      ...(destination ? { destination } : {}),
      budget:    String(budget),
      days:      String(days),
      travelers: String(travelers),
      mood:      extra.mood  ?? mood,
      mode:      extra.mode  ?? mode,
      ...(depDate ? { departure_date: depDate } : {}),
      ...(extra.destination ? { destination: extra.destination } : {}),
      // Pass editorial summary so the rearranged itinerary can surface it quietly
      ...(modifyReasoning?.summary
        ? { modification_note: modifyReasoning.summary }
        : {}),
    });
    window.location.href = `/itinerary?${params.toString()}`;
  };

  // ── Mobility preference regen ─────────────────────────────────────────────
  // Always derives the locked route from the current editedStays (authoritative
  // source of what the user sees) rather than from URL params. This prevents
  // stale fixed_cities / destination / open_constraints from overriding the
  // visible route on the backend.

  const handleMobilityRegenerate = (prefs: MobilityPref[]) => {
    // Always derive from editedStays — never trust raw URL params here, because
    // raw may still carry fixed_cities or destination from the original generation
    // flow. Mixing those with a new route_cities causes the backend to ignore the
    // full 3-city route and collapse to the stale fixed_cities value.
    const locked = editedStays.map((s, i) => ({
      id:          `stay-${i}`,
      city:        s.city,
      country:     s.country ?? "",
      countryCode: "",
      primaryIata: s.iata,
      allIatas:    [s.iata],
      nights:      s.nights,
      resolved:    true,
    }));
    const routeCitiesParam = JSON.stringify(locked);

    console.log("[MobilityRegenerate] route_cities sent:",
      locked.map(c => `${c.city}(${c.primaryIata})×${c.nights}n`).join(" → ")
    );

    // Build clean params using the already-resolved named variables (not raw),
    // so we get properly typed strings. Exclude destination, fixed_cities,
    // open_constraints — those must never re-enter during mobility regen.
    const params = new URLSearchParams({
      origin,
      region,
      budget:    String(budget),
      days:      String(days),
      travelers: String(travelers),
      mood,
      mode,
      ...(depDate ? { departure_date: depDate } : {}),
      route_cities: routeCitiesParam,
      ...(prefs.length ? { mobility_preference: prefs.join(",") } : {}),
    });
    window.location.href = `/itinerary?${params.toString()}`;
  };

  // ── Hotel replacement ─────────────────────────────────────────────────────

  const replaceHotel = (hotel: HotelOption, stayIndex: number) => {
    setEditedStays(prev => prev.map((s, i) =>
      i !== stayIndex ? s : {
        ...s,
        avgHotelPrice: hotel.price_per_night,
        hotel_name:    hotel.name,
        hotel_stars:   hotel.stars,
        hotel_type:    hotel.type,
        hotel_neighborhood: hotel.neighborhood,
      }
    ));
    toast.success(`🏨 ${hotel.name} seleccionado — costo actualizado`);
    setHotelModal(null);
  };

  // ── Transport replacement ─────────────────────────────────────────────────

  const replaceTransport = (option: TransportOption, legIndex: number) => {
    setEditedLegs(prev => prev.map((l, i) =>
      i !== legIndex ? l : {
        ...l,
        mode:          option.mode,
        selected_mode: option.mode,   // user override — mode_note is no longer valid
        mode_note:     undefined,     // clear RHEA note when user picks manually
        durationHours:  option.duration_hours,
        estimatedPrice: option.price,
        operator:       option.carrier,
      }
    ));
    toast.success(
      `${TRANSPORT_ICONS[option.mode] || "🚗"} ${option.carrier} seleccionado — costo actualizado`
    );
    setTransportModal(null);
  };

  // ── Activity CRUD ─────────────────────────────────────────────────────────

  const deleteActivity = (activityId: string) => {
    setEditedActivities(prev => prev.filter(a => a.id !== activityId));
    toast.success("Actividad eliminada");
  };

  const duplicateActivity = (activityId: string) => {
    setEditedActivities(prev => {
      const original = prev.find(a => a.id === activityId);
      if (!original) return prev;
      // Place duplicate at the end of its day so it doesn't collide with the original.
      const dayActivities = prev.filter(a => a.dayNumber === original.dayNumber);
      const maxOrder = dayActivities.length > 0
        ? Math.max(...dayActivities.map(a => a.order))
        : 0;
      return [...prev, {
        ...original,
        id:     generateActivityId(),
        source: "user_added" as const,
        order:  maxOrder + 1000,
      }];
    });
    toast.success("Actividad duplicada");
  };

  const moveActivity = (
    activityId: string,
    newDayNumber: number,
    newSlot: ActivitySlot,
    replace: boolean,
  ) => {
    setEditedActivities(prev => {
      // If replacing, remove the existing occupant of the target slot first
      const withoutConflict = replace
        ? prev.filter(a => !(a.dayNumber === newDayNumber && a.slot === newSlot && a.id !== activityId))
        : prev;
      // Place moved activity at the end of the target day's order.
      const targetDayActivities = withoutConflict.filter(
        a => a.dayNumber === newDayNumber && a.id !== activityId,
      );
      const maxOrder = targetDayActivities.length > 0
        ? Math.max(...targetDayActivities.map(a => a.order))
        : 0;
      return withoutConflict.map(a => {
        if (a.id !== activityId) return a;
        const targetDay = processedDays.find(d => d.dayNumber === newDayNumber);
        return {
          ...a,
          dayNumber: newDayNumber,
          slot:      newSlot,
          city:      targetDay?.city ?? a.city,
          order:     maxOrder + 1000,
        };
      });
    });
    toast.success("Actividad movida");
  };

  const addActivity = (activity: EditableActivity, replace: boolean) => {
    setEditedActivities(prev => {
      // If the user explicitly chose to replace via the collision banner in
      // AddActivityModal, honour it — but DnD never reaches this path.
      const withoutConflict = replace
        ? prev.filter(a => !(a.dayNumber === activity.dayNumber && a.slot === activity.slot))
        : prev;
      // Assign order at end of this day so new activity appears last
      const dayActivities = withoutConflict.filter(a => a.dayNumber === activity.dayNumber);
      const maxOrder = dayActivities.length > 0
        ? Math.max(...dayActivities.map(a => a.order)) + 1000
        : 0;
      return [...withoutConflict, { ...activity, order: maxOrder }];
    });
    toast.success("Actividad agregada");
  };

  /**
   * Replaces an activity in-place with the user's chosen suggestion.
   * Preserves dayNumber, slot, city, and editable flag from the original.
   * Sets source to "user_replaced" so the day card can render a subtle badge.
   */
  const changeActivity = (activityId: string, suggestion: ActivitySuggestion) => {
    let oldTitle = "";
    setEditedActivities(prev => prev.map(a => {
      if (a.id !== activityId) return a;
      oldTitle = a.title;
      return {
        ...a,
        title:         suggestion.title,
        type:          suggestion.type,
        description:   undefined,
        durationHours: suggestion.durationHours,
        area:          suggestion.area,
        source:        "user_replaced" as const,
      };
    }));
    toast.success(
      oldTitle
        ? `«${oldTitle}» reemplazado por «${suggestion.title}»`
        : "Actividad actualizada",
    );
  };

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  // These handlers are wired up only when ENABLE_ACTIVITY_DND (in DayCard.tsx)
  // is true. Keeping them here avoids dead-code removal so re-enabling is a
  // one-line change in DayCard.tsx.

  // Global safety net: clears stale dragState if dragend fires outside the
  // viewport (e.g. drop on desktop). Only registered when DnD is active.
  useEffect(() => {
    if (!ENABLE_ACTIVITY_DND_ITINERARY) return;
    const onWindowDragEnd = () => setDragState(null);
    window.addEventListener("dragend", onWindowDragEnd);
    return () => window.removeEventListener("dragend", onWindowDragEnd);
  }, []);

  const handleDragActivity = (state: DragState) => {
    setDragState(state);
  };

  const handleDragEndActivity = () => {
    setDragState(null);
  };

  /**
   * Called when the user drops into a DropBetweenZone.
   *
   * Insert-only policy: no existing activity is removed or overwritten.
   * The dragged activity is repositioned by updating its `order` and `dayNumber`.
   *
   * Order calculation:
   *   - insertAfterIdx = -1 → insert before the first activity in the day
   *   - insertAfterIdx = N  → insert after the N-th activity (0-based)
   *   - Uses midpoint between neighbours so repeated insertions stay precise.
   *   The midpoint can get arbitrarily small, but this is fine for MVP.
   *   A future "compact orders" pass can renormalise to integer multiples of 1000.
   */
  const handleDropBetween = (targetDayNumber: number, insertAfterIdx: number) => {
    if (!dragState) return;
    setDragState(null);

    setEditedActivities(prev => {
      const targetDay = processedDays.find(d => d.dayNumber === targetDayNumber);

      // Sorted activities in the target day, excluding the dragged one.
      const otherInDay = prev
        .filter(a => a.dayNumber === targetDayNumber && a.id !== dragState.activityId)
        .sort((a, b) => a.order - b.order);

      // ── Same-day index correction ─────────────────────────────────────────
      // DayCard passes insertAfterIdx using indices from sortedActivities, which
      // includes the dragged item itself. otherInDay excludes it. When the dragged
      // item sits BEFORE the drop zone in the same day, every index in otherInDay
      // is shifted left by 1 relative to what DayCard reported. Correct for this.
      let adjustedIdx = insertAfterIdx;
      if (dragState.sourceDayNumber === targetDayNumber && insertAfterIdx >= 0) {
        const allSortedInDay = prev
          .filter(a => a.dayNumber === targetDayNumber)
          .sort((a, b) => a.order - b.order);
        const draggedOrigIdx = allSortedInDay.findIndex(a => a.id === dragState.activityId);
        if (draggedOrigIdx >= 0 && draggedOrigIdx <= insertAfterIdx) {
          adjustedIdx = insertAfterIdx - 1;
        }
      }

      let newOrder: number;
      if (otherInDay.length === 0) {
        newOrder = 0;
      } else if (adjustedIdx < 0) {
        // Before the first item
        newOrder = otherInDay[0].order - 1000;
      } else if (adjustedIdx >= otherInDay.length - 1) {
        // After the last item
        newOrder = otherInDay[otherInDay.length - 1].order + 1000;
      } else {
        // Midpoint between two neighbours
        newOrder = (otherInDay[adjustedIdx].order + otherInDay[adjustedIdx + 1].order) / 2;
      }

      return prev.map(a => {
        if (a.id !== dragState.activityId) return a;
        return {
          ...a,
          dayNumber: targetDayNumber,
          city:      targetDay?.city ?? a.city,
          order:     newOrder,
        };
      });
    });

    toast.success(`«${dragState.sourceTitle}» reposicionado`);
  };

  // ── Derived cost ──────────────────────────────────────────────────────────

  const displayCost = useMemo(() => {
    if (!result || !editedStays.length) return result?.cost;
    const base     = result.cost;
    const lodging  = editedStays.reduce((sum, s) =>
      sum + (s.avgHotelPrice || 0) * (s.nights || 0), 0);
    const dailyExp = editedStays.reduce((sum, s) =>
      sum + (s.avgDailyBudget || 0) * (s.nights || 0) * travelers, 0);
    // Recalculate flights if user swapped a transport leg
    const flights = editedLegs.length > 0
      ? editedLegs.reduce((sum, l) =>
          sum + (l.estimatedPrice || 0) * travelers, 0)
      : base.flights;
    const buffer = Math.round((flights + lodging + dailyExp) * 0.05);
    const total  = flights + lodging + dailyExp + buffer;
    return { ...base, flights, lodging, daily_expenses: dailyExp, buffer, total };
  }, [editedStays, editedLegs, result, travelers]);

  const displayBudget = useMemo(() => {
    if (!displayCost || !result) return result?.budget;
    const pct = Math.round((displayCost.total / budget) * 100);
    return { ...result.budget, budget_used_pct: pct, within_budget: displayCost.total <= budget };
  }, [displayCost, result, budget]);

  // ── Itinerary context for modify-intent parser ───────────────────────────
  const modifyContext = useMemo<ItineraryContext | undefined>(() => {
    if (!result || editedStays.length === 0) return undefined;
    const totalN = editedStays.reduce((sum, s) => sum + (s.nights || 0), 0);
    return {
      cities:      editedStays.map(s => s.city),
      firstCity:   editedStays[0]?.city ?? "",
      lastCity:    editedStays[editedStays.length - 1]?.city ?? "",
      totalDays:   days,
      totalNights: totalN,
      mood,
      mode,
    };
  }, [result, editedStays, days, mood, mode]);

  // ── City contexts map (city name → CityContent) ───────────────────────────
  const cityContextsMap = useMemo<Record<string, CityContent>>(() => {
    if (!result?.city_contexts?.length) return {};
    return Object.fromEntries(result.city_contexts.map((c: CityContent) => [c.city, c]));
  }, [result]);

  // ── Must-see counts per city (iata → count) — fed to DecisionMap pins ─────
  const cityMustSeeCount = useMemo<Record<string, number>>(() => {
    if (!result?.city_contexts?.length || !result?.map_pins?.length) return {};
    const counts: Record<string, number> = {};
    for (const ctx of result.city_contexts) {
      const pin = result.map_pins.find((p: { city: string }) => p.city === ctx.city);
      if (pin?.iata) counts[pin.iata] = ctx.must_sees?.length ?? 0;
    }
    return counts;
  }, [result?.city_contexts, result?.map_pins]);

  // ── Processed timeline — mapCanonicalToViewModel ─────────────────────────
  // [StateNormalization] Single derivation path: mapCanonicalToViewModel reads
  // canonical.timeline_days (with embedded activities) via renderTimelineDay.
  // No structural decisions in this component.
  const processedDays = useMemo(() => {
    if (!result || !editableState.canonical) return [];
    const canonical = editableState.canonical;
    if (canonical.timeline_days.length === 0) return [];
    return mapCanonicalToViewModel(
      canonical,
      cityContextsMap,
      { mood: result.meta?.mood, mode: result.meta?.mode },
    );
  }, [result, editableState.canonical, cityContextsMap]);

  // ── Trip insights (detectors run after days are processed) ──────────────────
  const tripInsights = useMemo(() => {
    if (!result || !processedDays.length) return [];
    return analyzeTripInsights(
      editedStays,
      editedLegs,
      processedDays,
      cityContextsMap,
    ).insights;
  }, [result, editedStays, editedLegs, processedDays, cityContextsMap]);

  /**
   * Per-day insight lookup: at most one insight per day, the most severe
   * one whose stayIndex or dayNumber matches.
   * Priority: dayNumber match (overload) > stayIndex+firstDay match (booking/friction).
   */
  const insightByDay = useMemo<Record<number, TripInsight>>(() => {
    const map: Record<number, TripInsight> = {};
    for (const pDay of processedDays) {
      // Find best matching insight for this day (first in sorted list wins)
      const match = tripInsights.find(ins => {
        if (dismissedInsightIds.has(ins.id)) return false;
        // arrival_overload carries a dayNumber — match exactly
        if (ins.dayNumber !== undefined && ins.dayNumber === pDay.dayNumber) return true;
        // booking_urgency + transfer_friction carry stayIndex only — anchor to first day of that stay
        if (ins.dayNumber === undefined && ins.stayIndex !== undefined && ins.stayIndex === pDay.stayIndex && pDay.isFirstDayOfStay) return true;
        return false;
      });
      if (match) map[pDay.dayNumber] = match;
    }
    return map;
  }, [tripInsights, processedDays, dismissedInsightIds]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("¡Link copiado!");
  };

  const handleDownloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    toast("Generando PDF…", { icon: "⏳", duration: 7000 });
    try {
      const { generateRheaPdf, openPdfPrintTab } = await import("@/lib/pdfExport");

      // Resolve origin city display name from the first leg
      const originCity =
        (editedLegs.length > 0 ? editedLegs[0] : result.legs[0])?.fromCity ?? meta.origin;

      // Build the typed input — always use post-edit state
      const pdfInput: PdfExportInput = {
        originCity,
        region,
        totalDays:        processedDays.length,
        travelers,
        mood,
        mode,
        budget,
        summary:          result.summary,
        editedActivities,
        editedLegs:       editedLegs.length > 0 ? editedLegs : result.legs.map(l => ({ ...l })),
        editedStays,
        processedDays,
        tripInsights,
        cityContextsMap,
        cost: {
          flights:        cost.flights,
          lodging:        cost.lodging,
          daily_expenses: cost.daily_expenses,
          buffer:         cost.buffer,
          total:          cost.total,
        },
      };

      try {
        // Primary path: structured PDF via html2pdf.js
        await generateRheaPdf(pdfInput);
        toast.success("¡PDF descargado!");
      } catch (pdfErr) {
        // html2pdf failed (canvas error, timeout, OOM, etc.) — fall back to print tab.
        // The user gets the full styled HTML in a new tab and can use Ctrl+P → Save as PDF.
        console.error("[PDF] html2pdf failed, falling back to print tab:", pdfErr);
        toast("Abriendo itinerario en nueva pestaña — usa Ctrl+P para guardar como PDF", {
          icon: "🖨️",
          duration: 6000,
        });
        openPdfPrintTab(pdfInput);
      }
    } catch (e) {
      // Outer catch: import failed or pdfInput construction threw
      console.error("[PDF] Unexpected error in handleDownloadPdf:", e);
      toast.error("Error generando PDF. Intenta de nuevo.");
    } finally {
      // Always re-enable the button, even if everything above threw
      setPdfLoading(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      {/* Brand mark */}
      <span
        className="font-serif font-bold text-muted-foreground/30 mb-16 select-none"
        style={{ letterSpacing: "0.22em", fontSize: "11px" }}
      >
        SIFT TRIP
      </span>
      {/* Animated loading tip */}
      <AnimatePresence mode="wait">
        <motion.p
          key={tipIdx}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="text-base text-foreground/65 text-center max-w-xs leading-relaxed mb-12"
        >
          {TIPS[tipIdx]}
        </motion.p>
      </AnimatePresence>
      {/* Thin animated sweep line */}
      <div className="w-40 h-px bg-border overflow-hidden rounded-full">
        <motion.div
          className="h-full rounded-full"
          style={{ background: "oklch(0.298 0.058 167 / 0.5)", width: "50%" }}
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      {/* Route context */}
      <p className="text-[11px] text-muted-foreground/35 mt-10 tracking-wide">
        {editedStays.length > 0
          ? `${(editedLegs[0] || result?.legs?.[0])?.fromCity || origin} → ${editedStays.map(s => s.city).join(" → ")} → ${(editedLegs[0] || result?.legs?.[0])?.fromCity || origin}`
          : `${origin} → ${destination || region}`}
      </p>
    </div>
  );

  // ── T1 Coming Soon ────────────────────────────────────────────────────────

  if (t1Result) return (
    <ComingSoonScreen
      t1={t1Result}
      origin={origin}
      onExploreAlternative={handleExploreAlternative}
    />
  );

  // ── Error ─────────────────────────────────────────────────────────────────

  if (error || !result) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="rhea-card-static p-10 text-center max-w-md">
        <p className="text-3xl mb-4">😅</p>
        <p className="text-muted-foreground mb-6">{error || "No se pudo generar el itinerario."}</p>
        <div className="flex gap-3 justify-center">
          <button onClick={fetchItinerary} className="btn-gold">Reintentar</button>
          <Link to="/" className="px-4 py-2 rounded-lg border border-border text-sm font-semibold hover:bg-accent transition-colors">← Volver</Link>
        </div>
      </div>
    </div>
  );

  /** Editorial note passed via URL after a chip-triggered rearrangement. */
  const modificationNote: string = typeof raw.modification_note === "string" ? raw.modification_note : "";

  const { days: itinDays, legs, map_pins, summary, content_source, meta } = result;
  // Prefer result.resolver if present; fall back to meta fields so the badge
  // also appears when the backend is an older deploy that returns resolver data
  // inside meta instead of a top-level "resolver" key.
  const resolverInfo: ResolverInfo | undefined =
    result.resolver?.resolved_name
      ? result.resolver
      : result.meta?.resolved_destination
        ? { resolved_name: result.meta.resolved_destination }
        : undefined;
  const cost       = displayCost  || result.cost;
  const budgetInfo = displayBudget || result.budget;
  const totalNights = editedStays.reduce((sum, s) => sum + (s.nights || 0), 0);
  const cityNames   = editedStays.map(s => s.city).join(" → ");

  return (
    <div className="min-h-screen">

      {/* Hotel replacement panel */}
      <AnimatePresence>
        {hotelModal && (
          <HotelPanel
            modal={hotelModal}
            onClose={() => setHotelModal(null)}
            onSelect={replaceHotel}
          />
        )}
      </AnimatePresence>

      {/* Transport replacement drawer */}
      <AnimatePresence>
        {transportModal && (
          <LegReplacementDrawer
            modal={transportModal}
            onClose={() => setTransportModal(null)}
            onConfirm={replaceTransport}
          />
        )}
      </AnimatePresence>

      {/* ── Activity add drawer ───────────────────────────────────────────── */}
      <AnimatePresence>
        {addActivityModal && (() => {
          // Build Map<slot, title> for slots occupied on this day
          const occupied = new Map<ActivitySlot, string>(
            editedActivities
              .filter(a => a.dayNumber === addActivityModal.dayNumber)
              .map(a => [a.slot, a.title] as [ActivitySlot, string])
          );
          // Resolve CityContent for Tab A suggestions
          const addCityContent = result?.city_contexts?.find(
            (c: CityContent) => c.city === addActivityModal.city,
          );
          return (
            <ActivityAddDrawer
              dayNumber={addActivityModal.dayNumber}
              city={addActivityModal.city}
              cityContent={addCityContent}
              occupiedActivities={occupied}
              allActivities={editedActivities}
              mode={mood}
              onAdd={addActivity}
              onClose={() => setAddActivityModal(null)}
            />
          );
        })()}
      </AnimatePresence>

      <AnimatePresence>
        {moveActivityModal && (() => {
          const activity = editedActivities.find(a => a.id === moveActivityModal);
          if (!activity) return null;
          const availableDays = processedDays.map(d => ({
            dayNumber: d.dayNumber,
            city:      d.city,
            date:      d.date,
            // Build Map<slot, title> for collision detection
            occupiedActivities: new Map<ActivitySlot, string>(
              editedActivities
                .filter(a => a.dayNumber === d.dayNumber)
                .map(a => [a.slot, a.title] as [ActivitySlot, string])
            ),
          }));
          // Deduplicate — processedDays may have multiple entries per dayNumber
          const seen = new Set<number>();
          const uniqueDays = availableDays.filter(d => {
            if (seen.has(d.dayNumber)) return false;
            seen.add(d.dayNumber);
            return true;
          });
          return (
            <MoveActivityModal
              activity={activity}
              availableDays={uniqueDays}
              onMove={moveActivity}
              onClose={() => setMoveActivityModal(null)}
            />
          );
        })()}
      </AnimatePresence>

      {/* ── Activity suggestions drawer ───────────────────────────────────── */}
      <AnimatePresence>
        {changeActivityModal && (() => {
          const activity = editedActivities.find(a => a.id === changeActivityModal);
          if (!activity) return null;
          // Resolve city content for this activity's city
          const cityContent = result?.city_contexts?.find(
            (c: CityContent) => c.city === activity.city,
          );
          const suggestions = getSuggestions(activity, cityContent, editedActivities);
          return (
            <ActivitySuggestionsDrawer
              activity={activity}
              suggestions={suggestions}
              onSelect={s => {
                changeActivity(changeActivityModal, s);
                setChangeActivityModal(null);
              }}
              onClose={() => setChangeActivityModal(null)}
            />
          );
        })()}
      </AnimatePresence>

      {/* ── TOP BAR ───────────────────────────────────────────────────────── */}
      <div className="sticky top-16 z-30 bg-background/90 backdrop-blur border-b border-border/50 py-3 px-4 no-print">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/" className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">
              ← Cambiar búsqueda
            </Link>
            <span className="text-muted-foreground/20">|</span>
            <span className="text-xs text-muted-foreground/70">
              {editedStays.length > 0
                ? `${(editedLegs[0] || result?.legs?.[0])?.fromCity || meta.origin} → ${editedStays.map(s => s.city).join(" → ")} → ${(editedLegs[0] || result?.legs?.[0])?.fromCity || meta.origin}`
                : `${meta.origin || origin} → ${destination || region}`}
            </span>
          </div>
          <div className="flex gap-4 items-center">
            {mutationHistoryLength > 0 && (
              <button
                onClick={handleUndo}
                className="text-xs text-muted-foreground/50 hover:text-forest transition-colors flex items-center gap-1"
                title="Deshacer última edición"
              >
                ↩ Deshacer
              </button>
            )}
            <button onClick={handleShare}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">
              Compartir
            </button>
            <button onClick={handleDownloadPdf} disabled={pdfLoading}
              className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors disabled:opacity-30">
              {pdfLoading ? "Generando..." : "PDF"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20" id="itinerary-content">

        {/* ── TRIP SUMMARY HEADER ───────────────────────────────────────────── */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="mb-16">

          {/* Route breadcrumb: Origin → City1 → City2 → Origin */}
          {(() => {
            const originCity = (editedLegs[0] || result?.legs?.[0])?.fromCity || meta.origin;
            return (
              <div className="flex items-center gap-1.5 flex-wrap mb-6">
                <span className="text-[11px] text-muted-foreground/40 tracking-wide">{originCity}</span>
                {editedStays.map((s, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <span className="text-muted-foreground/20 text-xs">→</span>
                    <span className="text-[11px] text-muted-foreground/55 tracking-wide">{s.city}</span>
                  </span>
                ))}
                <span className="flex items-center gap-1.5">
                  <span className="text-muted-foreground/20 text-xs">→</span>
                  <span className="text-[11px] text-muted-foreground/35 tracking-wide">{originCity}</span>
                </span>
              </div>
            );
          })()}

          {/* Dominant destination headline */}
          <h1 className="font-serif text-5xl sm:text-6xl font-black leading-none mb-0">{cityNames}</h1>
          <div className="h-px mt-5 mb-7 bg-gradient-to-r from-gold/30 via-gold/10 to-transparent" />

          {/* Key stats — minimal, secondary */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-6 text-xs text-muted-foreground/60">
            <span>{totalNights} noches</span>
            <span className="text-border/60">·</span>
            <span>{editedStays.length} ciudad{editedStays.length !== 1 ? "es" : ""}</span>
            <span className="text-border/60">·</span>
            <span>{meta.travelers} viajero{meta.travelers > 1 ? "s" : ""}</span>
            <span className="text-border/60">·</span>
            <span className="font-medium text-foreground/50">
              ~${Math.round(cost.total || 0).toLocaleString()} estimado
            </span>
            <TrustBadge report={result.decision_report} />
          </div>

          {/* Summary — primary prose, comfortable reading size */}
          <p className="text-foreground/70 text-base leading-relaxed max-w-2xl mb-5">{summary}</p>

          {/* ── Solver editorial insight — shown when backend resolved open constraints ── */}
          {(() => {
            const reasons = result.meta?.solver_reasons;
            if (!reasons?.length) return null;
            const single = reasons.length === 1;
            return (
              <div
                className="mb-7 pl-4 max-w-2xl"
                style={{ borderLeft: "2px solid oklch(0.298 0.058 167 / 0.28)" }}
              >
                <p
                  className="text-[10.5px] tracking-wide mb-2"
                  style={{ color: "oklch(0.298 0.058 167 / 0.48)" }}
                >
                  {single ? "Por qué Sift Trip sugirió esta ciudad" : "Por qué Sift Trip sugirió estas ciudades"}
                </p>
                {single ? (
                  <p
                    className="text-[12.5px] leading-loose"
                    style={{ color: "oklch(0.298 0.058 167 / 0.65)" }}
                  >
                    {reasons[0]}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {reasons.map((r, i) => (
                      <li
                        key={i}
                        className="text-[12.5px] leading-loose"
                        style={{ color: "oklch(0.298 0.058 167 / 0.65)" }}
                      >
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

          {/* ── Editorial adjustment note — shown after a chip-triggered regen ── */}
          <AnimatePresence>
            {modificationNote && !noteDismissed && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="flex items-start gap-3 mb-5 px-4 py-3 rounded-xl"
                style={{
                  background: "oklch(0.298 0.058 167 / 0.05)",
                  border: "1px solid oklch(0.298 0.058 167 / 0.11)",
                }}
              >
                <span
                  className="text-[10px] mt-0.5 shrink-0 select-none"
                  style={{ color: "oklch(0.298 0.058 167 / 0.50)" }}
                >
                  ✦
                </span>
                <p
                  className="flex-1 text-[12px] leading-relaxed"
                  style={{ color: "oklch(0.298 0.058 167 / 0.72)" }}
                >
                  {modificationNote}
                </p>
                <button
                  onClick={() => setNoteDismissed(true)}
                  className="shrink-0 transition-colors"
                  style={{ color: "oklch(0.545 0.020 60 / 0.30)" }}
                  aria-label="Cerrar nota"
                >
                  ✕
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Mobility preferences — below summary, above timeline ─────────── */}
          <MobilityPreferences
            initialPrefs={mobilityPrefs}
            onRegenerate={handleMobilityRegenerate}
          />

          {/* Friction badge — pace + route quality, secondary */}
          <FrictionBadge report={result.travel_friction_report} />

          {/* Decision Panel — subtle expandable detail */}
          <DecisionPanel report={result.decision_report} />

          {/* ── Modify Bar ────────────────────────────────────────────────── */}
          <div className="mt-10 pt-7 border-t border-border/30">

            {/* Toggle — subtle text link */}
            <button
              onClick={() => setModifyOpen(v => !v)}
              className="text-xs text-muted-foreground/45 hover:text-muted-foreground/70 transition-colors"
            >
              {modifyOpen ? "Cerrar" : "¿Algo que ajustar?"}
              <span className="ml-1 opacity-60">{modifyOpen ? "↑" : "→"}</span>
            </button>

            {/* ── Quick-edit chips — always visible, immediately actionable ── */}
            <div className="flex flex-wrap gap-2 mt-4">
              {QUICK_CHIPS.map(chip => (
                <button
                  key={chip.label}
                  onClick={() => handleChipClick(chip)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    activeChip === chip.label
                      ? "border-forest/40 bg-forest/[0.09] text-forest"
                      : "border-border/55 bg-accent/25 hover:bg-accent hover:border-forest/25 text-foreground/60 hover:text-foreground/85"
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* ── Collapsible panel: free-text + reasoning ─────────────────── */}
            <AnimatePresence>
              {modifyOpen && (
                <motion.div
                  key="modify-panel"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="mt-5"
                >
                  {/* Free-text input */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={modifyText}
                      onChange={e => { setModifyText(e.target.value); setActiveChip(null); }}
                      onKeyDown={e => { if (e.key === "Enter") handleModify(modifyText); }}
                      placeholder="ej. más tiempo en Lisboa, algo romántico para la última noche, sin madrugones..."
                      className="flex-1 text-sm bg-background border border-border rounded-lg px-3 py-2 placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-forest/30 focus:border-forest/30"
                    />
                    <button
                      onClick={() => { setActiveChip(null); handleModify(modifyText); }}
                      disabled={!modifyText.trim()}
                      className="text-sm px-4 py-2 rounded-lg bg-forest text-forest-foreground font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    >
                      Ajustar
                    </button>
                  </div>

                  {/* Reasoning response */}
                  <AnimatePresence>
                    {modifyReasoning && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="mt-3 flex items-start gap-3 rounded-xl px-4 py-3.5"
                        style={{
                          background: "oklch(0.298 0.058 167 / 0.05)",
                          border: "1px solid oklch(0.298 0.058 167 / 0.12)",
                        }}
                      >
                        <span
                          className="text-sm mt-0.5 shrink-0 select-none"
                          style={{ color: "oklch(0.298 0.058 167 / 0.45)" }}
                        >
                          ✦
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-relaxed"
                            style={{ color: "oklch(0.138 0.010 55 / 0.72)" }}>
                            {modifyReasoning.reasoning}
                          </p>
                          <button
                            onClick={handleRegenerate}
                            className="mt-2.5 text-sm font-semibold transition-colors"
                            style={{ color: "oklch(0.298 0.058 167 / 0.85)" }}
                          >
                            Aplicar ajuste →
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            setModifyReasoning(null);
                            setModifyText("");
                            setActiveChip(null);
                          }}
                          className="text-muted-foreground/35 hover:text-muted-foreground/65 transition-colors shrink-0 mt-0.5"
                        >
                          ✕
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* ── DESTINATION MOOD ────────────────────────────────────────────────── */}
        {result.city_contexts?.[0] && (() => {
          const ctx = result.city_contexts![0];
          const avgNightsPerCity = editedStays.length > 0 ? totalNights / editedStays.length : 0;
          const frictionLevel = result.travel_friction_report?.friction_level;
          const ritmoLabel =
            frictionLevel === "low"  ? "Ritmo cómodo" :
            frictionLevel === "high" ? "Paso intenso"  : "Ritmo equilibrado";
          const ritmoDetail = `${(Math.round(avgNightsPerCity * 10) / 10)} noches por ciudad en promedio`;

          const expHeadline = ctx.must_sees?.length >= 2
            ? `${ctx.must_sees[0].name} · ${ctx.must_sees[1].name}`
            : ctx.must_sees?.[0]?.name || ctx.tagline;
          const expDetail = ctx.must_sees?.[0]?.why || ctx.local_tip;

          const allLegs = editedLegs.length > 0 ? editedLegs : legs;
          const modeCounts = allLegs.reduce<Record<string, number>>((acc, l) => {
            acc[l.mode] = (acc[l.mode] || 0) + 1; return acc;
          }, {});
          const dominantMode = Object.entries(modeCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
          const rutaHeadline =
            dominantMode === "flight" ? "Vuelos entre ciudades" :
            dominantMode === "train"  ? "En tren" :
            dominantMode === "bus"    ? "En bus"  : "Ruta multimodal";
          const rutaDetail = `${editedStays.length} ciudad${editedStays.length !== 1 ? "es" : ""} · ${totalNights} noches`;

          // ── Resolve editorial image for the primary city ─────────────────────
          const editorialImg = getEditorialImage(editedStays[0]?.city || "");
          const showHeroImage = editorialImg !== null && !heroImageError;

          // Shared city label used in both image and typographic panel
          const cityLabel = editedStays[0]?.city + (
            editedStays.length > 1
              ? ` · ${editedStays.length - 1} destino${editedStays.length > 2 ? "s" : ""} más`
              : ""
          );

          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.55 }}
              className="mb-20"
            >
              {/* ── Atmospheric panel — image or typographic fallback ───────── */}
              {showHeroImage ? (

                // ── Image version ─────────────────────────────────────────────
                <div
                  className="relative overflow-hidden rounded-2xl mb-10 min-h-[260px] sm:min-h-[340px]"
                >
                  {/* Curated editorial image — fades in on load */}
                  <img
                    src={editorialImg!.hero}
                    alt={editorialImg!.alt}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      objectPosition: editorialImg!.focalPoint,
                      opacity: 0,
                      transition: "opacity 0.8s ease",
                    }}
                    onLoad={e => {
                      (e.target as HTMLImageElement).style.opacity = "1";
                    }}
                    onError={() => setHeroImageError(true)}
                  />

                  {/* Left-to-right gradient overlay — protects text, lets right side breathe */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to right, rgba(10,8,6,0.60) 0%, rgba(10,8,6,0.28) 52%, rgba(10,8,6,0.05) 100%)",
                    }}
                  />

                  {/* Bottom vignette — softens lower edge on tall mobile crops */}
                  <div
                    className="absolute inset-x-0 bottom-0 h-24"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(10,8,6,0.30) 0%, transparent 100%)",
                    }}
                  />

                  {/* Content — warm white on dark overlay */}
                  <div className="relative z-10 p-8 sm:p-10 max-w-xl">
                    <p
                      className="font-serif text-[10px] font-bold tracking-[0.16em] uppercase mb-3"
                      style={{ color: "rgba(255,255,255,0.50)" }}
                    >
                      {cityLabel}
                    </p>
                    <h2
                      className="font-serif text-2xl sm:text-3xl font-bold leading-snug mb-4"
                      style={{ color: "rgba(255,255,255,0.93)" }}
                    >
                      {ctx.tagline}
                    </h2>
                    <p
                      className="text-sm leading-relaxed max-w-sm"
                      style={{ color: "rgba(255,255,255,0.65)" }}
                    >
                      {ctx.local_tip}
                    </p>
                  </div>

                  {/* Subtle photo credit — bottom-right, almost invisible */}
                  {editorialImg!.credit && (
                    <p
                      className="absolute bottom-3 right-4 text-[9px] select-none"
                      style={{ color: "rgba(255,255,255,0.20)" }}
                    >
                      {editorialImg!.credit}
                    </p>
                  )}
                </div>

              ) : (

                // ── Typographic fallback — no image available or load failed ──
                <div
                  className="relative overflow-hidden rounded-2xl mb-10 min-h-[260px] sm:min-h-[340px]"
                  style={{
                    background:
                      "linear-gradient(150deg, oklch(0.930 0.018 72) 0%, oklch(0.952 0.013 68) 100%)",
                  }}
                >
                  {/* Ghosted city name — huge serif background texture */}
                  <div
                    className="absolute inset-0 flex items-center justify-end pr-8 select-none pointer-events-none overflow-hidden"
                    aria-hidden
                  >
                    <span
                      className="font-serif font-black leading-none"
                      style={{
                        fontSize: "clamp(5rem, 14vw, 11rem)",
                        color: "oklch(0.138 0.010 55 / 0.04)",
                      }}
                    >
                      {editedStays[0]?.city || ""}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-8 sm:p-10 max-w-xl">
                    <p
                      className="font-serif text-[10px] font-bold tracking-[0.14em] uppercase mb-3"
                      style={{ color: "oklch(0.298 0.058 167 / 0.55)" }}
                    >
                      {cityLabel}
                    </p>
                    <h2
                      className="font-serif text-2xl sm:text-3xl font-bold leading-snug mb-4"
                      style={{ color: "oklch(0.138 0.010 55 / 0.82)" }}
                    >
                      {ctx.tagline}
                    </h2>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "oklch(0.545 0.020 60 / 0.85)" }}
                    >
                      {ctx.local_tip}
                    </p>
                  </div>
                </div>

              )}

              {/* 3 quiet highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
                {([
                  { label: "Ritmo",        headline: ritmoLabel,    detail: ritmoDetail },
                  { label: "Experiencias", headline: expHeadline,   detail: expDetail   },
                  { label: "Ruta",         headline: rutaHeadline,  detail: rutaDetail  },
                ] as { label: string; headline: string; detail: string | undefined }[]).map(({ label, headline, detail }) => (
                  <div key={label} className="pl-4 border-l"
                    style={{ borderColor: "oklch(0.298 0.058 167 / 0.18)" }}>
                    <p className="text-[10px] font-medium tracking-[0.12em] uppercase mb-1.5"
                      style={{ color: "oklch(0.545 0.020 60 / 0.5)" }}>
                      {label}
                    </p>
                    <p className="text-sm font-medium mb-1"
                      style={{ color: "oklch(0.138 0.010 55 / 0.72)" }}>
                      {headline}
                    </p>
                    {detail && (
                      <p className="text-xs leading-relaxed"
                        style={{ color: "oklch(0.545 0.020 60 / 0.6)" }}>
                        {detail}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* ── Trip timeline strip — reads from editable state (stays current after mutations) */}
              {(() => {
                const tl = editableTripTimeline ?? result.trip_timeline;
                if (!tl) return null;
                return (
                  <div
                    className="mt-6 pt-5 flex items-center gap-3 flex-wrap"
                    style={{ borderTop: "1px solid oklch(0.298 0.058 167 / 0.10)" }}
                  >
                    <span
                      className="text-[13px] font-medium"
                      style={{ color: "oklch(0.138 0.010 55 / 0.65)" }}
                    >
                      {tl.date_label}
                    </span>
                    {tl.is_inferred && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full"
                        style={{
                          color:      "oklch(0.545 0.020 60 / 0.58)",
                          background: "oklch(0.545 0.020 60 / 0.07)",
                          border:     "1px solid oklch(0.545 0.020 60 / 0.12)",
                        }}
                      >
                        Fechas estimadas
                      </span>
                    )}
                    {tl.start_date && tl.end_date && (
                      <span
                        className="text-[11px]"
                        style={{ color: "oklch(0.545 0.020 60 / 0.42)" }}
                      >
                        {fmtDate(tl.start_date)} – {fmtDate(tl.end_date)}
                      </span>
                    )}
                    {tl.temporal_qa?.calendar_note && (
                      <span
                        className="text-[11px] w-full mt-1"
                        style={{ color: "oklch(0.545 0.020 60 / 0.52)" }}
                      >
                        {tl.temporal_qa.calendar_note}
                      </span>
                    )}
                  </div>
                );
              })()}
            </motion.div>
          );
        })()}

        {/* ── PLANNING LAYER ──────────────────────────────────────────────────── */}
        <div className="flex flex-col xl:flex-row gap-8">

          {/* LEFT: Route map — collapsed by default */}
          <div className={`shrink-0 transition-all duration-300 ${mapExpanded ? "xl:w-72" : "xl:w-44"}`}>
            {map_pins?.length > 0 && (
              <div className="xl:sticky xl:top-36">
                {/* Header row — always visible */}
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[11px] text-muted-foreground/45 font-medium tracking-wide">
                    Tu ruta
                  </h3>
                  <button
                    onClick={() => setMapExpanded(v => !v)}
                    className="text-[11px] text-muted-foreground/40 hover:text-forest transition-colors"
                  >
                    {mapExpanded ? "← Ocultar" : "Ver mapa →"}
                  </button>
                </div>

                {/* Collapsed: minimal city list */}
                {!mapExpanded && (
                  <div className="space-y-2.5">
                    {editedStays.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const iata = s.iata || map_pins?.[i]?.iata;
                          if (iata) {
                            setSelectedCityIata(iata);
                            const el = document.querySelector(`[data-city-iata="${iata}"]`);
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        className="flex items-center gap-2.5 w-full text-left group"
                      >
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 transition-colors"
                          style={{
                            background: selectedCityIata === (s.iata || map_pins?.[i]?.iata)
                              ? "oklch(0.298 0.058 167 / 0.15)"
                              : "oklch(0.298 0.058 167 / 0.07)",
                            color: "oklch(0.298 0.058 167 / 0.8)",
                          }}
                        >
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <span className="text-[11px] font-medium text-foreground/65 group-hover:text-foreground/90 transition-colors truncate block">
                            {s.city}
                          </span>
                          <span className="text-[10px] text-muted-foreground/40">{s.nights}n</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Expanded: full DecisionMap + city pills */}
                {mapExpanded && (
                  <>
                    {transportModal && (
                      <span className="inline-block mb-2 text-[10px] font-medium text-muted-foreground/60 bg-accent/60 border border-border/50 rounded-full px-2 py-0.5">
                        Tramo activo
                      </span>
                    )}
                    <DecisionMap
                      legs={editedLegs.length > 0 ? editedLegs : legs}
                      stays={editedStays}
                      mapPins={map_pins.map(p => ({
                        city: p.city, lat: p.lat ?? 0, lng: p.lng ?? 0, iata: p.iata,
                        nights: p.nights, order: p.order,
                      }))}
                      hoveredLegIndex={hoveredLegIndex}
                      openLegIndex={transportModal?.legIndex ?? null}
                      selectedCityIata={selectedCityIata}
                      cityMustSeeCount={cityMustSeeCount}
                      onCityClick={(iata) => {
                        setSelectedCityIata(iata);
                        const el = document.querySelector(`[data-city-iata="${iata}"]`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      onLegClick={(idx) => {
                        setHoveredLegIndex(idx);
                        const el = document.querySelector(`[data-leg-index="${idx}"]`);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="mb-3"
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {editedStays.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            const iata = s.iata || map_pins?.[i]?.iata;
                            if (iata) {
                              setSelectedCityIata(iata);
                              const el = document.querySelector(`[data-city-iata="${iata}"]`);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }
                          }}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium ${
                            selectedCityIata === (s.iata || map_pins?.[i]?.iata)
                              ? "border-forest/50 bg-forest/10 text-forest"
                              : "border-border/50 bg-accent/30 text-muted-foreground/70 hover:border-forest/25 hover:text-foreground"
                          }`}
                        >
                          {i + 1}. {s.city} · {s.nights}n
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* CENTER: day timeline */}
          <div className="flex-1 space-y-6">
            <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-muted-foreground/35">
              Día a día
            </p>
            {processedDays.map((pDay, i) => {
              const legIndices = pDay.events
                .filter(e => e.legIndex !== undefined)
                .map(e => e.legIndex as number);
              const cityIata   = map_pins?.find(p => p.city === pDay.city)?.iata ?? "";
              const stayNights = editedStays[pDay.stayIndex]?.nights ?? 1;
              const isLoading  = mutationLoading[cityIata] ?? false;

              return (
                <div
                  key={pDay.dayNumber}
                  data-city-iata={cityIata}
                  {...(legIndices.length > 0 ? { "data-leg-index": legIndices[0] } : {})}
                >
                  {/* Inline stay controls — shown only on first day of each stay */}
                  {pDay.isFirstDayOfStay && cityIata && (
                    <div className="flex items-center gap-2 mb-2 no-print">
                      <span className="text-[10px] text-muted-foreground/40 font-medium">
                        {pDay.city}
                      </span>
                      <div className="flex items-center gap-1">
                        {isLoading ? (
                          <span className="text-[9px] text-muted-foreground/35 animate-pulse px-1">
                            editando…
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleMutation({ type: "remove_days", city_iata: cityIata, delta: 1 }, cityIata)}
                              disabled={stayNights <= 1}
                              className="w-5 h-5 rounded flex items-center justify-center text-[11px] text-muted-foreground/40 hover:text-muted-foreground hover:bg-accent/60 transition-colors disabled:opacity-20"
                              title={`Un día menos en ${pDay.city}`}
                            >−</button>
                            <span className="text-[9px] text-muted-foreground/35 tabular-nums px-0.5">
                              {stayNights}n
                            </span>
                            <button
                              onClick={() => handleMutation({ type: "add_days", city_iata: cityIata, delta: 1 }, cityIata)}
                              className="w-5 h-5 rounded flex items-center justify-center text-[11px] text-muted-foreground/40 hover:text-muted-foreground hover:bg-accent/60 transition-colors"
                              title={`Un día más en ${pDay.city}`}
                            >+</button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <DayCard
                    day={pDay}
                    travelers={travelers}
                    insight={insightByDay[pDay.dayNumber]}
                    onDismissInsight={handleDismissInsight}
                    onHoverLeg={setHoveredLegIndex}
                    onSwapTransport={(legIndex) => {
                      const leg = (editedLegs.length > 0 ? editedLegs : legs)[legIndex];
                      if (!leg) return;
                      setTransportModal({
                        legIndex,
                        fromCity:        leg.fromCity,
                        toCity:          leg.toCity,
                        fromIata:        leg.fromIata || "",
                        toIata:          leg.toIata   || "",
                        departureDate:   depDate || null,
                        travelers,
                        currentMode:     leg.mode,
                        currentPrice:    leg.estimatedPrice || 0,
                        currentDuration: leg.durationHours  || 0,
                      });
                    }}
                    onSwapHotel={(stayIndex) => {
                      const s = editedStays[stayIndex];
                      if (!s) return;
                      setHotelModal({
                        stayIndex,
                        cityIata:     s.iata,
                        cityName:     s.city,
                        checkin:      s.checkin || "",
                        checkout:     s.checkout || "",
                        nights:       s.nights || 1,
                        travelers,
                        currentPrice: s.avgHotelPrice || 0,
                      });
                    }}
                    activities={editedActivities.filter(a => a.dayNumber === pDay.dayNumber)}
                    onMoveActivity={id => setMoveActivityModal(id)}
                    onChangeActivity={id => setChangeActivityModal(id)}
                    onDeleteActivity={deleteActivity}
                    onDuplicateActivity={duplicateActivity}
                    onAddActivity={(dayNumber, city) => setAddActivityModal({ dayNumber, city })}
                    dragState={ENABLE_ACTIVITY_DND_ITINERARY ? dragState : null}
                    onDragActivity={ENABLE_ACTIVITY_DND_ITINERARY ? handleDragActivity : undefined}
                    onDropBetween={ENABLE_ACTIVITY_DND_ITINERARY ? handleDropBetween : undefined}
                    onDragEndActivity={ENABLE_ACTIVITY_DND_ITINERARY ? handleDragEndActivity : undefined}
                    defaultOpen={i < 2}
                  />
                </div>
              );
            })}
          </div>

          {/* RIGHT: budget + nights — softened sidebar */}
          <div className="xl:w-64 shrink-0 space-y-5 xl:pt-7">

            {/* Budget widget */}
            <div className="px-5 py-5 rounded-xl" style={{ background: "oklch(0.990 0.007 72 / 0.7)" }}>
              <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-muted-foreground/40 mb-4">
                Estimación
              </p>
              <div className="space-y-2 text-sm">
                {[
                  { label: "Vuelos",         val: cost.flights        },
                  { label: "Hospedaje",      val: cost.lodging        },
                  { label: "Gastos diarios", val: cost.daily_expenses },
                ].map(({ label, val }) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-muted-foreground/60">{label}</span>
                    <span className="font-medium text-foreground/65">${Math.round(val || 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-border/40 pt-3 mt-2 flex justify-between items-baseline">
                  <span className="text-xs text-muted-foreground/50">Total estimado</span>
                  <motion.span
                    key={Math.round(cost.total)}
                    initial={{ opacity: 0.5, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-base font-semibold text-foreground/75"
                  >
                    ${Math.round(cost.total || 0).toLocaleString()}
                  </motion.span>
                </div>
                <p className="text-[10px] text-muted-foreground/35 pt-0.5">
                  Confirma precios al reservar
                </p>
              </div>
            </div>

            {/* City nights stepper — Editable Timeline v1 */}
            <div className="px-5 py-5 rounded-xl" style={{ background: "oklch(0.990 0.007 72 / 0.7)" }}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-medium tracking-[0.1em] uppercase text-muted-foreground/40">
                  Noches por ciudad
                </p>
                <span className="text-[10px] text-muted-foreground/35">{totalNights} total</span>
              </div>
              <div className="space-y-1">
                {editedStays.map((s, i) => {
                  const cityKey   = s.iata || String(i);
                  const isLoading = mutationLoading[cityKey] ?? false;
                  return (
                    <div key={i} className="flex items-center gap-2.5 py-1.5">
                      <span className="text-[10px] text-muted-foreground/30 font-medium w-3 shrink-0 text-right tabular-nums">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground/70">{s.city}</div>
                        {s.checkin && s.checkout && (
                          <div className="text-[10px] text-muted-foreground/40">
                            {fmtDate(s.checkin)} – {fmtDate(s.checkout)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isLoading ? (
                          <span className="w-16 text-center text-[10px] text-muted-foreground/40 animate-pulse">
                            editando…
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleMutation({ type: "remove_days", city_iata: s.iata, delta: 1 }, cityKey)}
                              disabled={s.nights <= 1}
                              className="w-6 h-6 rounded-md bg-input/80 hover:bg-accent flex items-center justify-center text-xs font-bold transition-colors text-muted-foreground disabled:opacity-30"
                            >−</button>
                            <motion.span
                              key={s.nights}
                              initial={{ scale: 1.2 }} animate={{ scale: 1 }}
                              className="w-7 text-center text-xs font-medium tabular-nums text-foreground/65"
                            >{s.nights}n</motion.span>
                            <button
                              onClick={() => handleMutation({ type: "add_days", city_iata: s.iata, delta: 1 }, cityKey)}
                              className="w-6 h-6 rounded-md bg-input/80 hover:bg-accent flex items-center justify-center text-xs font-bold transition-colors text-muted-foreground"
                            >+</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* PDF */}
            <div className="no-print">
              <button
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                className="w-full text-xs text-muted-foreground/45 hover:text-muted-foreground/70 transition-colors py-2 text-center"
              >
                {pdfLoading ? "Generando..." : "Descargar PDF"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
